import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useOptionalAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { palette as T, alpha, type } from '../design';

// The dashboard menu for phones (#307, KP-5). At 768 px or less a Menu button opens every tab
// of the role, Back to site and Log out. Wider screens keep the side menu, so this renders nothing.

const FONT = "'Plus Jakarta Sans', sans-serif";

const LOGOUT_FAILED = 'We could not log you out. Check your connection and try again.';

// Shows the home page and signs out. The home page comes first: the app sends a signed-out
// person on a dashboard to the login page, so signing out there first lands them on login.
// A failed sign-out says so; the person is still signed in and can log out from the site menu.
function useLogout(setPage) {
  // The same context as useAuth(); this form does not throw where a test renders a dashboard alone.
  const auth = useOptionalAuth();
  const { showSnackbar } = useSnackbar();
  const [busy, setBusy] = React.useState(false);

  const logout = async () => {
    if (busy) return;
    setBusy(true);
    setPage('home');
    try {
      await auth.logout();
    } catch (err) {
      console.error('[DashboardMenu] log out failed', err);
      showSnackbar(LOGOUT_FAILED, { variant: 'error' });
      setBusy(false);
    }
  };
  return { logout, busy };
}

// Log out for the side menu, placed under Back to site.
export function DashboardLogoutButton({ setPage }) {
  const { logout, busy } = useLogout(setPage);
  return (
    <button type="button" onClick={logout} disabled={busy}
      style={{ background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.errorRed, display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginTop: 12 }}>
      {busy ? 'Logging out…' : 'Log out'}
    </button>
  );
}

export function DashboardMenu({ tabs, active, onSelect, setPage }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const { logout, busy } = useLogout(setPage);
  const panelId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isMobile) return null;

  const activeLabel = tabs.find((t) => t.id === active)?.label || '';
  const itemStyle = (isActive) => ({
    width: '100%', padding: '14px 20px', border: 'none', textAlign: 'left', cursor: 'pointer',
    background: isActive ? T.ivory : 'transparent',
    borderLeft: isActive ? '3px solid ' + T.terracotta : '3px solid transparent',
    fontFamily: FONT, fontSize: 15, fontWeight: isActive ? 700 : 500,
    color: isActive ? T.terracotta : T.espresso,
  });

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 900, background: T.ivory, backdropFilter: 'blur(12px)', borderBottom: '1px solid ' + T.border }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 16px' }}>
        <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: 15, color: T.espresso, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeLabel}</span>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={panelId}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', minHeight: 40, borderRadius: 10, border: '1px solid ' + T.border, background: T.white, cursor: 'pointer', fontFamily: FONT, fontWeight: 600, fontSize: type.body, color: T.espresso }}>
          <span aria-hidden="true">{open ? '✕' : '☰'}</span>
          Menu
        </button>
      </div>
      {open && (
        <nav id={panelId} aria-label="Dashboard menu"
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.white, borderBottom: '1px solid ' + T.border, boxShadow: '0 12px 24px ' + alpha('espresso', 'shadow'), maxHeight: 'calc(100vh - 60px)', overflowY: 'auto', padding: '6px 0' }}>
          {tabs.map((tab) => (
            <button key={tab.id} type="button" aria-current={tab.id === active ? 'page' : undefined}
              onClick={() => { onSelect(tab.id); setOpen(false); }}
              style={itemStyle(tab.id === active)}>
              {tab.label}
            </button>
          ))}
          <div style={{ borderTop: '1px solid ' + T.border, margin: '6px 0' }} />
          <button type="button" onClick={() => { setOpen(false); setPage('home'); }}
            style={{ ...itemStyle(false), color: T.walnut, fontSize: type.body }}>
            ← Back to site
          </button>
          <button type="button" onClick={logout} disabled={busy}
            style={{ ...itemStyle(false), color: T.errorRed, fontSize: type.body, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Logging out…' : 'Log out'}
          </button>
        </nav>
      )}
    </div>
  );
}
